import React, { useState, useEffect } from 'react';
import { ArrowRight, TrendingUp, Users, Target, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mock';

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    setTimeout(() => setStatsVisible(true), 500);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const icons = [TrendingUp, Users, Target, Globe];

  return (
    <section id="hero" className="relative min-h-screen bg-gradient-to-br from-[#0B0B0B] via-[#0B0B0B] to-[#1a1a1a] overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Headline with animation */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
              style={{
                animation: 'fadeInUp 0.8s ease-out',
                opacity: 1
              }}
            >
              {heroData.headline}
            </h1>

            {/* Subheadline */}
            <p 
              className="text-lg sm:text-xl text-white/70 leading-relaxed"
              style={{
                animation: 'fadeInUp 0.8s ease-out 0.3s both'
              }}
            >
              {heroData.subheadline}
            </p>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
              {heroData.stats.map((stat, index) => {
                const Icon = icons[index];
                return (
                  <div
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1"
                    style={{
                      animation: `fadeInUp 0.6s ease-out ${0.5 + index * 0.1}s both`
                    }}
                  >
                    <Icon className="text-[#2563EB] mb-2" size={20} />
                    <div className="text-2xl font-bold text-white mb-1">
                      {statsVisible && stat.value}
                    </div>
                    <div className="text-xs text-white/60">{stat.label}</div>
                  </div>
                );
              })}
            </div>

            {/* CTA Button */}
            <div
              style={{
                animation: 'fadeInUp 0.8s ease-out 0.9s both'
              }}
            >
              <Button
                onClick={scrollToContact}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 shadow-xl hover:shadow-[#2563EB]/50 group"
                style={{
                  animation: 'pulse 3s ease-in-out infinite'
                }}
              >
                {heroData.ctaText}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-200" size={20} />
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div 
            className="relative"
            style={{
              transform: `translateY(${scrollY * 0.2}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#2563EB]/20 to-transparent z-10" />
              <img
                src={heroData.heroImage}
                alt="Fitness Creator"
                className="w-full h-auto object-cover"
                style={{
                  animation: 'fadeIn 1s ease-out 0.5s both'
                }}
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -z-10 top-10 -right-10 w-72 h-72 bg-[#2563EB]/20 rounded-full blur-3xl" />
            <div className="absolute -z-10 -bottom-10 -left-10 w-72 h-72 bg-[#2563EB]/10 rounded-full blur-3xl" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); }
          50% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
